// generateCanonicalAndAlternates.js
'use strict';
import { PREFERRED_LOCALES } from '../resource/i18nCommon.js';
import { getI18nInstance } from '../resource/i18n.js';
import { alternateDomainMap } from '../resource/seoConstants.js';
import {
  generateLangPath,
  getHreflang,
  normalizeLang,
  removeLangFromPath,
} from '../resource/langPath.js';

/**
 * Generate canonical URL and alternate language links for SEO
 * @param {Object} options
 * @param {string} options.canonicalPath - The canonical path (e.g., '/user/123')
 * @param {string|Array} options.availableLanguages - Available languages (string or array)
 * @returns {Object} Object with canonical and alternates
 */
const generateCanonicalAndAlternates = ({
  canonicalPath,
  availableLanguages = PREFERRED_LOCALES,
}) => {
  if (!canonicalPath) {
    return {
      canonical: null,
      alternates: null,
    };
  }
  const { i18n } = getI18nInstance();
  const currentLanguage = i18n.language;

  // remove language prefix from path, get clean path
  const cleanPath = removeLangFromPath(canonicalPath);

  // Prepare canonical URL (use default language, traditional Chinese, no prefix)
  const currentHostname = alternateDomainMap.default;
  const normalizedLang = normalizeLang(currentLanguage);
  const canonical = new URL(
    `${normalizedLang === 'zh-TW' ? '' : `/${normalizedLang}`}${cleanPath}`,
    `https://${currentHostname}`
  ).href;

  // Prepare alternate language links with lang-prefixed paths
  const alternates = [];
  if (availableLanguages) {
    const languages =
      typeof availableLanguages === 'string'
        ? availableLanguages.split(' ').filter(Boolean)
        : Array.isArray(availableLanguages)
          ? availableLanguages
          : [];

    languages.forEach(lang => {
      const langHostname =
        alternateDomainMap[lang.toLowerCase()] || alternateDomainMap.default;
      // use generateLangPath to generate path with language prefix
      const langPath = generateLangPath(cleanPath, lang);

      let hreflang = getHreflang(lang);

      alternates.push({
        hreflang: hreflang,
        href: `https://${langHostname}${langPath}`,
      });
    });

    // Add x-default (use default language path, no prefix)
    const xDefaultHostname = alternateDomainMap.default;
    alternates.push({
      hreflang: 'x-default',
      href: `https://${xDefaultHostname}${cleanPath}`,
    });
  }

  return {
    canonical,
    alternates,
  };
};

export default generateCanonicalAndAlternates;
